// src/pages/OS.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore, collection, doc, getDocs, getDoc, query, where,
  Timestamp, increment, updateDoc, deleteDoc, runTransaction, serverTimestamp,
  deleteField
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import axios from 'axios';
import { auth } from '../firebaseConfig';
import { useEmpresaId } from '../context/EmpresaContext';

import { Link } from 'react-router-dom';
import ModalNovaOS from '../components/ModalNovaOS';
import OSCard from '../components/OS/OSCard';
import OSTable from '../components/OS/OSTable';
import OSDetalheModal from '../components/OS/OSDetalheModal';
import ModalNotasFiscais from '../components/OS/ModalNotasFiscais';

import { tempoDesde, formatarData } from '../utils/format';
import { FaPlus, FaTable, FaThLarge } from 'react-icons/fa';
import { Search, BookOpen } from 'lucide-react';

const db = getFirestore();

/* =========================
   NOTAS FISCAIS (helpers)
   ========================= */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
const DEBUG_NF = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DEBUG_NF === '1';
const nfLog = (...args) => { if (DEBUG_NF) console.info('[NF]', ...args); };

/* NEW: fallback viewer para NFS-e */
const nfseViewUrl = (ref) =>
  `${BACKEND_URL}/api/focus/visualizar-nfse/${encodeURIComponent(ref)}`;

async function authHeader() {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

async function emitirNFe(empresaId, os) {
  nfLog('Emitir NF-e - OS', os?.id);
  const body = {
    osId: os.id,
    clienteId: os.clienteId,
    produtos: os.produtos || [],
    natureza_operacao: 'Venda de mercadoria',
  };
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/emissao-nfe/${empresaId}`,
    body,
    { headers: await authHeader() }
  );
  nfLog('NF-e emitida/resposta', data);
  return data;
}

/* >>> genérico: aceita ref OU chave (backend aceita ambos no mesmo endpoint) <<< */
async function consultarNFe(id, empresaId) {
  nfLog('Consultar NF-e - id', id);
  const { data } = await axios.get(
    `${BACKEND_URL}/api/focus/consultar-nfe/${encodeURIComponent(id)}`,
    { headers: await authHeader(), params: { empresaId } }
  );
  nfLog('Consulta NF-e/retorno', data);
  return data;
}

async function cancelarNFe(empresaId, osId, justificativa) {
  nfLog('Cancelar NF-e - OS', osId);
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/cancelar-nfe/${empresaId}`,
    { osId, justificativa },
    { headers: await authHeader() }
  );
  nfLog('Cancelamento NF-e/retorno', data);
  return data;
}

async function emitirNFSe(empresaId, os) {
  nfLog('Emitir NFS-e - OS', os?.id);
  const body = {
    osId: os.id,
    clienteId: os.clienteId,
    servicos: os.servicos || [],
  };
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/emissao-nfse/${empresaId}`,
    body,
    { headers: await authHeader() }
  );
  nfLog('NFS-e emitida/resposta', data);
  return data;
}

async function consultarNFSePorRef(ref) {
  nfLog('Consultar NFS-e - ref', ref);
  const { data } = await axios.get(
    `${BACKEND_URL}/api/focus/consultar-nfse/${encodeURIComponent(ref)}`,
    { headers: await authHeader() }
  );
  nfLog('Consulta NFS-e/retorno', data);
  return data;
}

async function cancelarNFSe(empresaId, osId, justificativa) {
  nfLog('Cancelar NFS-e - OS', osId);
  const { data } = await axios.post(
    `${BACKEND_URL}/api/focus/cancelar-nfse/${empresaId}`,
    { osId, justificativa },
    { headers: await authHeader() }
  );
  nfLog('Cancelamento NFS-e/retorno', data);
  return data;
}

/* =========================
   ESTÁTICOS / HELPERS
   ========================= */
const statusColors = {
  Aberta: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900',
  'Em andamento': 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-900',
  Finalizada: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900',
  Cancelada: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800'
};

// chips de visualização do status NF-e/NFS-e
export const nfStatusView = (status = '') => {
  const s = String(status || '').toLowerCase();

  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('autoriz')) return { text: 'Autorizada', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('deneg')) return { text: 'Denegada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha'))
    return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };

  if (s.includes('process'))
    return { text: 'Processando', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  if (s.includes('aguard') || s.includes('pend'))
    return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };

  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};

export const nfseStatusView = (status = '') => {
  const s = String(status || '').toLowerCase();
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('autoriz') || s.includes('emitida') || s.includes('aprov'))
    return { text: 'Emitida', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha'))
    return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process'))
    return { text: 'Processando', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  if (s.includes('aguard') || s.includes('pend'))
    return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};

const defaultForm = {
  clienteId: '', veiculoId: '', status: 'Aberta', dataAbertura: '',
  produtos: [], servicos: [], observacoes: '',
  valorTotalProdutos: 0, valorTotalServicos: 0, valorTotalGeral: 0,
};

const camposNota = [
  'nfeEmitida', 'nfeRef', 'nfeStatus', 'nfeDanfeUrl', 'nfeChave',
  'nfeNumero', 'nfeSerie', 'nfeXmlUrl', 'nfeUltimoErro',
  'nfseEmitida', 'nfseRef', 'nfseStatus', 'nfseUrl', 'nfseUltimoErro',
  'nfceEmitida'
];

/* NEW: usa fallback viewer se não houver nfseUrl e passa pdf/html quando houver */
const getNotasFiscaisFromOS = (os) => {
  if (!os) return [];
  const notas = [];
  if (os.nfeRef || os.nfeEmitida) {
    notas.push({
      tipo: 'nfe',
      status: os.nfeStatus,
      numero: os.nfeNumero,
      serie: os.nfeSerie,
      chave: os.nfeChave,
      linkDanfe: os.nfeChave ? `${BACKEND_URL}/api/focus/baixar-danfe/${os.nfeChave.replace(/^NFe/, '')}` : '',
      linkXml: os.nfeChave ? `${BACKEND_URL}/api/focus/baixar-xml/${os.nfeChave.replace(/^NFe/, '')}` : '',
      podeCancelar: os.nfeEmitida && os.nfeStatus !== 'cancelada',
      dataEmissao: os.nfeDataEmissao || '',
      ultimoErro: os.nfeUltimoErro || '',
    });
  }
  if (os.nfseRef || os.nfseEmitida) {
    const view = os.nfseUrl || (os.nfseRef ? nfseViewUrl(os.nfseRef) : '');
    notas.push({
      tipo: 'nfse',
      status: os.nfseStatus,
      numero: os.nfseNumero,
      serie: os.nfseSerie,
      chave: os.nfseRef,
      link: view,
      linkPdf: os.nfsePdfUrl || '',
      linkHtml: os.nfseHtmlUrl || '',
      podeCancelar: os.nfseEmitida && os.nfseStatus !== 'cancelada',
      dataEmissao: os.nfseDataEmissao || '',
      ultimoErro: os.nfseUltimoErro || '',
    });
  }
  return notas;
};

// helpers de caminho para OS (subcoleção)
const colOS = (empresaId) => collection(db, 'empresas', empresaId, 'os');
const refOS = (empresaId, osId) => doc(db, 'empresas', empresaId, 'os', osId);
const refContadorOS = (empresaId) => doc(db, 'empresas', empresaId, 'contadores', 'os');

export default function OS() {
  const empresaId = useEmpresaId();

  const [osList, setOsList] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesPath, setClientesPath] = useState('auto'); // 'nested' | 'top' | 'auto'
  const [veiculos, setVeiculos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [empresaInfo, setEmpresaInfo] = useState(null);

  const [produtosPath, setProdutosPath] = useState('auto'); // 'nested' | 'top' | 'auto'
  const [servicosPath, setServicosPath] = useState('auto'); // 'nested' | 'top' | 'auto'

  const [form, setForm] = useState(defaultForm);
  const [editandoOs, setEditandoOs] = useState(null);

  const [busca, setBusca] = useState('');
  const [activeTab, setActiveTab] = useState('abertas');
  const [viewMode, setViewMode] = useState('cards');

  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [formVisivel, setFormVisivel] = useState(false);
  const [detalheAberto, setDetalheAberto] = useState(null);
  const [modalNotasOpen, setModalNotasOpen] = useState(false);
  const [osNotasSelecionada, setOsNotasSelecionada] = useState(null);

  const [nfseLoading, setNfseLoading] = useState({});
  const [nfeLoading, setNfeLoading] = useState({});
  const [loadingCancelNota, setLoadingCancelNota] = useState({});
  const [loadingRefreshNota, setLoadingRefreshNota] = useState({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (empresaId) {
      fetchAllData(empresaId);
      fetchEmpresaInfo(empresaId);
    } else {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    const valorTotalProdutos = (form.produtos || []).reduce((acc, p) => acc + (Number(p.precoVenda) || 0) * (Number(p.quantidade) || 0), 0);
    const valorTotalServicos = (form.servicos || []).reduce((acc, s) => acc + (Number(s.valor) || 0), 0);
    setForm(f => ({ ...f, valorTotalProdutos, valorTotalServicos, valorTotalGeral: valorTotalProdutos + valorTotalServicos }));
  }, [form.produtos, form.servicos]);

  const fetchEmpresaInfo = async (id) => {
    try {
      const snap = await getDoc(doc(db, 'empresas', id));
      if (snap.exists) setEmpresaInfo({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error('Erro ao buscar info da empresa', e);
    }
  };

  // Busca preferindo subcoleção e caindo para top-level (retorna também o path)
  const fetchPreferNestedWithPath = async (empresaIdParam, nomeColecao) => {
    const nestedRef = collection(db, 'empresas', empresaIdParam, nomeColecao);
    const nestedSnap = await getDocs(nestedRef);
    if (!nestedSnap.empty) {
      return {
        path: 'nested',
        data: nestedSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      };
    }
    const topQ = query(collection(db, nomeColecao), where('empresaId', '==', empresaIdParam));
    const topSnap = await getDocs(topQ);
    return {
        path: 'top',
        data: topSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    };
  };

  const fetchAllData = async (empresaIdParam) => {
    setLoading(true);

    const tryFetchClientes = async () => {
      const nested = collection(db, 'empresas', empresaIdParam, 'clientes');
      const nestedSnap = await getDocs(nested);
      if (!nestedSnap.empty) {
        return { path: 'nested', data: nestedSnap.docs.map(d => ({ id: d.id, ...d.data() })) };
      }
      const topQ = query(collection(db, 'clientes'), where('empresaId', '==', empresaIdParam));
      const topSnap = await getDocs(topQ);
      return { path: 'top', data: topSnap.docs.map(d => ({ id: d.id, ...d.data() })) };
    };

    try {
      const { path, data } = await tryFetchClientes();
      setClientesPath(path);
      setClientes(data);

      // veículos conforme path descoberto
      let veics = [];
      for (const c of data) {
        const ref = path === 'nested'
          ? collection(db, 'empresas', empresaIdParam, 'clientes', c.id, 'veiculos')
          : collection(db, 'clientes', c.id, 'veiculos');
        const snap = await getDocs(ref);
        const items = snap.docs.map(d => ({ id: d.id, clienteId: c.id, ...d.data() }));
        veics = veics.concat(items);
      }
      setVeiculos(veics);

      // produtos/serviços (prefer nested) — salva também o path
      const { path: prodPath, data: prods } = await fetchPreferNestedWithPath(empresaIdParam, 'produtos');
      const { path: servPath, data: servs } = await fetchPreferNestedWithPath(empresaIdParam, 'servicos');
      setProdutosPath(prodPath);
      setServicosPath(servPath);
      setProdutos(prods);
      setServicos(servs);

      await buscarOS(empresaIdParam);
    } catch (e) {
      console.error('Erro ao buscar dados essenciais', e);
      Swal.fire('Erro', 'Não foi possível carregar os dados necessários.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buscarOS = async (empresaIdParam) => {
    if (!empresaIdParam) return setOsList([]);
    try {
      const snap = await getDocs(colOS(empresaIdParam));
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => (b?.dataAbertura?.toDate?.() || 0) - (a?.dataAbertura?.toDate?.() || 0));
      setOsList(arr);

      // Mantém o modal sincronizado com a OS atualizada
      if (osNotasSelecionada) {
        const atualizada = arr.find(o => o.id === osNotasSelecionada.id);
        if (atualizada) setOsNotasSelecionada(atualizada);
      }
    } catch (e) {
      console.error('Erro ao buscar OS', e);
      Swal.fire('Erro', 'Ocorreu um erro ao buscar as Ordens de Serviço.', 'error');
    }
  };

  const handleNovaOS = () => {
    setForm(defaultForm);
    setEditandoOs(null);
    setFormVisivel(true);
  };

  // criação com contador
  async function criarOSComNumero(empresaIdParam, dadosOS) {
    const contadorRef = refContadorOS(empresaIdParam);
    const novaOSRef = doc(colOS(empresaIdParam));
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(contadorRef);
      let proximo = 1;
      if (!snap.exists()) {
        tx.set(contadorRef, { ultimo: proximo, atualizadoEm: serverTimestamp() });
      } else {
        const atual = Number(snap.data().ultimo || 0);
        proximo = atual + 1;
        tx.update(contadorRef, { ultimo: proximo, atualizadoEm: serverTimestamp() });
      }
      tx.set(novaOSRef, { ...dadosOS, numeroOS: proximo });
    });
    return novaOSRef.id;
  }

  const handleSalvarOS = async () => {
    try {
      const cliente = clientes.find(c => c.id === form.clienteId);
      const veiculo = veiculos.find(v => v.id === form.veiculoId);

      const basePayload = {
        ...form,
        empresaId,
        clienteNome: cliente?.nome || '',
        veiculoDescricao: veiculo ? `${veiculo.placa} - ${veiculo.modelo}` : '',
        dataAbertura: form.dataAbertura || Timestamp.now(),
      };

      if (editandoOs) {
        const payload = { ...basePayload };
        camposNota.forEach(k => delete payload[k]);
        await updateDoc(refOS(empresaId, editandoOs.id), payload);
        Swal.fire('Sucesso!', 'OS atualizada!', 'success');
      } else {
        const payload = {
          ...basePayload,
          nfeEmitida: false, nfseEmitida: false, nfseUrl: '', nfseStatus: '',
          nfseRef: '', nfceEmitida: false
        };
        await descontarEstoqueProdutos(form.produtos);
        await criarOSComNumero(empresaId, payload);
        Swal.fire('Sucesso!', 'OS cadastrada!', 'success');
      }

      setFormVisivel(false);
      await buscarOS(empresaId);
    } catch (err) {
      console.error('Erro ao salvar OS', err);
      if (err?.code === 'permission-denied') {
        Swal.fire('Sem permissão', 'Seu usuário não tem permissão para gravar nesta coleção. Verifique o claim empresaId e as regras do Firestore.', 'error');
      } else {
        Swal.fire('Erro', 'Não foi possível salvar a OS.', 'error');
      }
    }
  };

  const handleEditarOS = (os) => {
    const safe = { ...os };
    camposNota.forEach(f => delete safe[f]);
    setEditandoOs(os);
    setForm({
      ...safe,
      dataAbertura: os.dataAbertura,
      produtos: os.produtos || [],
      servicos: os.servicos || []
    });
    setFormVisivel(true);
  };

  const handleExcluirOS = async (os) => {
    const r = await Swal.fire({
      title: 'Excluir OS?',
      text: 'Tem certeza? Isso não poderá ser desfeito!',
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar'
    });
    if (r.isConfirmed) {
      try {
        await devolverEstoqueProdutos(os.produtos || []);
        await deleteDoc(refOS(empresaId, os.id));
        await buscarOS(empresaId);
        Swal.fire('Excluída!', 'A OS foi removida.', 'success');
      } catch (err) {
        console.error('Erro ao excluir OS', err);
        Swal.fire('Erro', 'Não foi possível excluir a OS.', 'error');
      }
    }
  };

  const handleUpdateStatus = async (os, novoStatus) => {
    const r = await Swal.fire({
      title: `Alterar status para "${novoStatus}"?`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sim, alterar', cancelButtonText: 'Cancelar'
    });
    if (r.isConfirmed) {
      try {
        await updateDoc(refOS(empresaId, os.id), { status: novoStatus });
        await buscarOS(empresaId);
      } catch (err) {
        console.error('Erro ao atualizar status', err);
        Swal.fire('Erro', 'Não foi possível alterar o status.', 'error');
      }
    }
  };

  async function descontarEstoqueProdutos(lista) {
    if (!Array.isArray(lista) || !lista.length) return;
    for (const p of lista) {
      if (!p.produtoId || !p.quantidade) continue;
      try {
        const ref =
          produtosPath === 'nested'
            ? doc(db, 'empresas', empresaId, 'produtos', p.produtoId)
            : doc(db, 'produtos', p.produtoId);
        await updateDoc(ref, { estoque: increment(-Number(p.quantidade)) });
      } catch (err) {
        console.error('Erro ao descontar estoque', err);
        throw err;
      }
    }
  }

  async function devolverEstoqueProdutos(lista) {
  if (!Array.isArray(lista) || !lista.length) return;

  console.log('produtosPath atual:', produtosPath, 'empresaId:', empresaId);

  for (const p of lista) {
    if (!p.produtoId || !p.quantidade) continue;
    try {
      const ref =
        produtosPath === 'nested'
          ? doc(db, 'empresas', empresaId, 'produtos', p.produtoId)
          : doc(db, 'produtos', p.produtoId);

      console.log('Atualizando estoque para:', ref.path);
      await updateDoc(ref, { estoque: increment(Number(p.quantidade)) });
    } catch (err) {
      console.error('Erro ao devolver estoque', err);
      throw err;
    }
  }
}

  /* ================
     Utils NFS-e (UI)
     ================ */
  const buildNfseHtml = (resp, ref) => {
    const status = resp?.status || '-';
    const numero = resp?.numero || '';
    const serie = resp?.serie || '';
    const rps = [resp?.numero_rps, resp?.serie_rps].filter(Boolean).join(' / ');
    const viewUrl = resp?.view_url || (ref ? nfseViewUrl(ref) : '');
    const pdfUrl = resp?.pdf_url || '';
    const htmlUrl = resp?.html_url || '';
    const links = Array.isArray(resp?.links) ? resp.links : [];

    const linkTag = (href, label) =>
      href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>` : '';

    const extras = [
      linkTag(viewUrl, 'Abrir NFS-e'),
      linkTag(pdfUrl, 'PDF'),
      linkTag(htmlUrl, 'HTML')
    ].filter(Boolean).join(' &middot; ');

    const outros = links
      .filter(u => u !== viewUrl && u !== pdfUrl && u !== htmlUrl)
      .map(u => `<li><a href="${u}" target="_blank" rel="noopener noreferrer">${u}</a></li>`)
      .join('');

    return `
      <div style="text-align:left">
        <p><strong>Status:</strong> ${status}</p>
        ${numero || serie ? `<p><strong>Número/Série:</strong> ${numero || '-'} / ${serie || '-'}</p>` : ''}
        ${rps ? `<p><strong>RPS:</strong> ${rps}</p>` : ''}
        ${extras ? `<p style="margin-top:8px">${extras}</p>` : ''}
        ${outros ? `<details style="margin-top:8px"><summary>Outros links</summary><ul>${outros}</ul></details>` : ''}
      </div>
    `;
  };

  /* =========================
     NOTAS - handlers (front)
     ========================= */
  const handleEmitirNota = async (os, tipo) => {
    const shouldNFe  = tipo === 'nfe' || (tipo === undefined && (os.produtos || []).length > 0);
    const shouldNFSe = !shouldNFe && (tipo === 'nfse' || (tipo === undefined && (os.servicos || []).length > 0));

    if (!shouldNFe && !shouldNFSe) {
      return Swal.fire('Atenção', 'OS sem produtos e sem serviços. Nada a emitir.', 'info');
    }

    try {
      if (shouldNFe) {
        setNfeLoading(prev => ({ ...prev, [os.id]: true }));
        const r = await emitirNFe(empresaId, os);
        Swal.fire('NF-e', r?.mensagem || 'Envio realizado. Consulte o status em alguns segundos.', 'success');
      } else if (shouldNFSe) {
        setNfseLoading(prev => ({ ...prev, [os.id]: true }));
        const r = await emitirNFSe(empresaId, os);
        Swal.fire('NFS-e', r?.mensagem || 'Envio realizado. Consulte o status em alguns segundos.', 'success');
      }
      await buscarOS(empresaId);
    } catch (err) {
      console.error(err);

      const api = err?.response?.data;
      const det = api?.detalhes || api?.erro || {};
      const errosList = Array.isArray(det?.erros)
        ? det.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
        : '';
      const msg =
        api?.mensagem ||
        det?.mensagem_sefaz ||
        det?.mensagem ||
        err.message ||
        'Falha ao emitir nota.';

      try {
        if (shouldNFe) {
          await updateDoc(refOS(empresaId, os.id), {
            nfeUltimoErro: `${msg}${errosList ? `\n${errosList}` : ''}`,
            nfeStatus: 'rejeitada',
            nfeEmitida: false,
          });
        } else if (shouldNFSe) {
          await updateDoc(refOS(empresaId, os.id), {
            nfseUltimoErro: `${msg}${errosList ? `\n${errosList}` : ''}`,
            nfseStatus: 'rejeitada',
            nfseEmitida: false,
          });
        }
        await buscarOS(empresaId);
      } catch (_) {}

      Swal.fire('Erro', `${msg}${errosList ? `\n\n${errosList}` : ''}`, 'error');
    } finally {
      setNfeLoading(prev => ({ ...prev, [os.id]: false }));
      setNfseLoading(prev => ({ ...prev, [os.id]: false }));
    }
  };

  // CONSULTA NF-e — agora aceita ref OU chave
  const handleConsultarNfe = async (os) => {
    try {
      const chave = (os.nfeChave || '').replace(/^NFe/, '');
      const id = os.nfeRef || chave;
      if (!id) return Swal.fire('NF-e', 'Esta OS ainda não possui referência ou chave de NF-e.', 'info');

      setNfeLoading(prev => ({ ...prev, [os.id]: true }));

      const resp = await consultarNFe(id, empresaId);
      const statusLower = String(resp?.status || resp?.nfeStatus || '').toLowerCase();

      try {
        await updateDoc(refOS(empresaId, os.id), {
          nfeStatus: resp?.status || os.nfeStatus || '',
          nfeChave: (resp?.chave_nfe || os.nfeChave || '').replace(/^NFe/, ''),
          nfeNumero: resp?.numero || os.nfeNumero || '',
          nfeSerie: resp?.serie || os.nfeSerie || '',
          nfeDanfeUrl: resp?.caminho_danfe || os.nfeDanfeUrl || '',
          nfeXmlUrl: resp?.caminho_xml_nota_fiscal || os.nfeXmlUrl || '',
          nfeEmitida: /(autoriz|emitid|aprov)/i.test(statusLower) && !/cancelad/.test(statusLower),
          nfeUltimoErro: deleteField()
        });
      } catch (_) {}

      if (/process|aguard|pend/.test(statusLower)) {
        Swal.fire('NF-e', 'A SEFAZ ainda está processando a autorização. Tente novamente em instantes.', 'info');
      } else if (/erro|rejei|deneg/.test(statusLower)) {
        const detalhes = resp?.detalhes || resp;
        const erros = Array.isArray(detalhes?.erros)
          ? detalhes.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
          : '';
        const msg = detalhes?.mensagem_sefaz || detalhes?.mensagem || `NF-e com status "${resp?.status}"`;
        try { await updateDoc(refOS(empresaId, os.id), { nfeUltimoErro: `${msg}${erros ? `\n${erros}` : ''}` }); } catch {}
        Swal.fire('NF-e', `${msg}${erros ? `\n\n${erros}` : ''}`, 'error');
      } else {
        Swal.fire('NF-e', 'Consulta realizada.', 'success');
      }
    } catch (err) {
      const api = err?.response?.data;
      const erros = Array.isArray(api?.erro?.erros)
        ? api.erro.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
        : Array.isArray(api?.detalhes?.erros)
          ? api.detalhes.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
          : '';
      const msg =
        api?.mensagem ||
        api?.erro?.mensagem ||
        api?.detalhes?.mensagem ||
        err.message ||
        'Falha ao consultar NF-e.';
      try { await updateDoc(refOS(empresaId, os.id), { nfeUltimoErro: `${msg}${erros ? `\n${erros}` : ''}` }); } catch {}
      Swal.fire('Erro', `${msg}${erros ? `\n\n${erros}` : ''}`, 'error');
    } finally {
      await buscarOS(empresaId);
      setNfeLoading(prev => ({ ...prev, [os.id]: false }));
    }
  };

  // CONSULTA NFS-e — agora mostra links (view/pdf/html) e persiste
  const handleConsultarNfse = async (os) => {
    try {
      if (!os.nfseRef) return Swal.fire('NFS-e', 'Esta OS ainda não possui referência de NFS-e.', 'info');
      setNfseLoading(prev => ({ ...prev, [os.id]: true }));
      setLoadingRefreshNota(prev => ({ ...prev, [os.nfseRef]: true, nfse: true }));

      const resp = await consultarNFSePorRef(os.nfseRef);
      const statusLower = String(resp?.status || '').toLowerCase();

      const viewUrl = resp?.view_url || os.nfseUrl || nfseViewUrl(os.nfseRef);
      const pdfUrl = resp?.pdf_url || '';
      const htmlUrl = resp?.html_url || '';
      const links = Array.isArray(resp?.links) ? resp.links : [];

      try {
        await updateDoc(refOS(empresaId, os.id), {
          nfseStatus: resp?.status || os.nfseStatus || '',
          nfseId: resp?.id || os.nfseId || '',
          nfseNumero: resp?.numero || os.nfseNumero || '',
          nfseSerie: resp?.serie || os.nfseSerie || '',
          nfseNumeroRps: resp?.numero_rps || os.nfseNumeroRps || '',
          nfseSerieRps: resp?.serie_rps || os.nfseSerieRps || '',
          nfseTipoRps: resp?.tipo_rps || os.nfseTipoRps || '',
          nfseUrl: viewUrl,
          nfsePdfUrl: pdfUrl || deleteField(),
          nfseHtmlUrl: htmlUrl || deleteField(),
          nfseLinks: links.length ? links : deleteField(),
          nfseEmitida: /(autoriz|emitid|aprov)/i.test(statusLower) && !/cancelad/.test(statusLower),
          nfseUltimoErro: deleteField()
        });
      } catch (_) {}

      if (/process|aguard|pend/.test(statusLower)) {
        Swal.fire('NFS-e', 'A prefeitura ainda está processando a autorização. Tente novamente em instantes.', 'info');
      } else if (/erro|rejei|deneg/.test(statusLower)) {
        const erros = Array.isArray(resp?.erros)
          ? resp.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
          : '';
        const msg = resp?.mensagem || resp?.mensagem_sefaz || `NFS-e com status "${resp?.status}"`;
        try { await updateDoc(refOS(empresaId, os.id), { nfseUltimoErro: `${msg}${erros ? `\n${erros}` : ''}` }); } catch {}
        Swal.fire('NFS-e', `${msg}${erros ? `\n\n${erros}` : ''}`, 'error');
      } else {
        Swal.fire({
          title: 'NFS-e',
          html: buildNfseHtml(resp, os.nfseRef),
          icon: 'success'
        });
      }
    } catch (err) {
      const api = err?.response?.data;
      const erros = Array.isArray(api?.erro?.erros)
        ? api.erro.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
        : Array.isArray(api?.detalhes?.erros)
          ? api.detalhes.erros.map(e => `${e?.codigo || ''} ${e?.mensagem || ''}`).join('\n')
          : '';
      const msg =
        api?.mensagem ||
        api?.erro?.mensagem ||
        api?.detalhes?.mensagem ||
        err.message ||
        'Falha ao consultar NFS-e.';
      try { await updateDoc(refOS(empresaId, os.id), { nfseUltimoErro: `${msg}${erros ? `\n${erros}` : ''}` }); } catch {}
      Swal.fire('Erro', `${msg}${erros ? `\n\n${erros}` : ''}`, 'error');
    } finally {
      await buscarOS(empresaId);
      setNfseLoading(prev => ({ ...prev, [os.id]: false }));
      setLoadingRefreshNota(prev => ({ ...prev, [os.nfseRef]: false, nfse: false }));
    }
  };

  // Chamado pelo ModalNotasFiscais — recebe o objeto "nota"
  const handleCancelarNota = async (nota) => {
    const tipo = nota?.tipo; // 'nfe' | 'nfse'
    if (!tipo) return;

    try {
      const { value: justificativa } = await Swal.fire({
        title: `Cancelar ${tipo.toUpperCase()}?`,
        input: 'textarea',
        inputPlaceholder: 'Descreva a justificativa (mín. 15 caracteres)',
        showCancelButton: true,
        confirmButtonText: 'Cancelar',
        cancelButtonText: 'Voltar',
      });
      if (!justificativa) return;

      setLoadingCancelNota(prev => ({
        ...prev,
        [tipo]: true,
        ...(nota.chave ? { [nota.chave]: true } : {})
      }));

      const osId = osNotasSelecionada?.id;
      if (!osId) throw new Error('OS não localizada para cancelamento.');

      if (tipo === 'nfe') {
        await cancelarNFe(empresaId, osId, justificativa);
        Swal.fire('NF-e', 'Cancelada com sucesso.', 'success');
      } else {
        await cancelarNFSe(empresaId, osId, justificativa);
        Swal.fire('NFS-e', 'Cancelada com sucesso.', 'success');
      }
      await buscarOS(empresaId);
    } catch (err) {
      console.error(err);
      Swal.fire('Erro', err?.response?.data?.mensagem || 'Falha ao cancelar.', 'error');
    } finally {
      setLoadingCancelNota(prev => ({
        ...prev,
        [nota?.tipo]: false,
        ...(nota?.chave ? { [nota.chave]: false } : {})
      }));
    }
  };

  // Chamado pelo ModalNotasFiscais — recebe o objeto "nota"
  // Agora, para NFS-e mostra links; para NF-e aceita ref OU chave
  const handleAtualizarNota = async (nota) => {
    try {
      setLoadingRefreshNota(prev => ({
        ...prev,
        [nota?.tipo]: true,
        ...(nota?.chave ? { [nota.chave]: true } : {})
      }));

      if (nota?.tipo === 'nfse') {
        const ref = nota?.chave || osNotasSelecionada?.nfseRef;
        if (!ref) return Swal.fire('NFS-e', 'Referência não encontrada.', 'info');

        const resp = await consultarNFSePorRef(ref);

        try {
          const viewUrl = resp?.view_url || nfseViewUrl(ref);
          await updateDoc(refOS(empresaId, osNotasSelecionada.id), {
            nfseStatus: resp?.status || '',
            nfseNumero: resp?.numero || '',
            nfseSerie: resp?.serie || '',
            nfseNumeroRps: resp?.numero_rps || '',
            nfseSerieRps: resp?.serie_rps || '',
            nfseTipoRps: resp?.tipo_rps || '',
            nfseUrl: viewUrl,
            nfsePdfUrl: resp?.pdf_url || deleteField(),
            nfseHtmlUrl: resp?.html_url || deleteField(),
            nfseLinks: Array.isArray(resp?.links) && resp.links.length ? resp.links : deleteField(),
            nfseUltimoErro: deleteField()
          });
        } catch(_) {}

        Swal.fire({
          title: 'NFS-e',
          html: buildNfseHtml(resp, ref),
          icon: 'success'
        });
      } else if (nota?.tipo === 'nfe') {
        const chave = (osNotasSelecionada?.nfeChave || '').replace(/^NFe/, '');
        const id = osNotasSelecionada?.nfeRef || chave;
        if (!id) return Swal.fire('NF-e', 'Referência ou chave (nfeChave) não encontrada nesta OS.', 'info');

        try {
          const resp = await consultarNFe(id, empresaId);
          const statusLower = String(resp?.status || '').toLowerCase();

          await updateDoc(refOS(empresaId, osNotasSelecionada.id), {
            nfeStatus: resp?.status || '',
            nfeChave: (resp?.chave_nfe || '').replace(/^NFe/, ''),
            nfeNumero: resp?.numero || '',
            nfeSerie: resp?.serie || '',
            nfeDanfeUrl: resp?.caminho_danfe || '',
            nfeXmlUrl: resp?.caminho_xml_nota_fiscal || '',
            nfeEmitida: /(autoriz|emitid|aprov)/i.test(statusLower) && !/cancelad/.test(statusLower),
            nfeUltimoErro: deleteField()
          });

          if (/process|aguard|pend/.test(statusLower)) {
            Swal.fire('NF-e', 'A SEFAZ ainda está processando a autorização. Tente novamente em instantes.', 'info');
          } else {
            Swal.fire('NF-e', 'Status atualizado.', 'success');
          }
        } catch (e) {
          const api = e?.response?.data || {};
          const det = api?.detalhes || api?.erro || {};
          const erros = Array.isArray(det?.erros)
            ? det.erros.map(er => `${er?.codigo || ''} ${er?.mensagem || ''}`).join('\n')
            : '';
          const msg =
            api?.mensagem ||
            det?.mensagem_sefaz ||
            det?.mensagem ||
            e.message ||
            'Erro ao atualizar status da NF-e.';

          try {
            await updateDoc(refOS(empresaId, osNotasSelecionada.id), {
              nfeStatus: det?.status || 'rejeitada',
              nfeUltimoErro: `${msg}${erros ? `\n${erros}` : ''}`
            });
          } catch(_) {}

          Swal.fire('NF-e', `${msg}${erros ? `\n\n${erros}` : ''}`, 'error');
        }
      }

      await buscarOS(empresaId);
    } catch (err) {
      console.error(err);
      Swal.fire('Erro', err?.response?.data?.mensagem || 'Falha ao atualizar status.', 'error');
    } finally {
      setLoadingRefreshNota(prev => ({
        ...prev,
        [nota?.tipo]: false,
        ...(nota?.chave ? { [nota.chave]: false } : {})
      }));
    }
  };

  const handleAbrirNotasFiscais = (os) => {
    setOsNotasSelecionada(os);
    setModalNotasOpen(true);
  };

  // filtro
  const osFiltradas = useMemo(() => {
    let arr = osList.filter(os => (activeTab === 'abertas' ? os.status !== 'Finalizada' : os.status === 'Finalizada'));
    if (activeTab === 'historico' && (dataInicio || dataFim)) {
      arr = arr.filter(os => {
        if (!os.dataAbertura?.toDate) return false;
        const d = os.dataAbertura.toDate();
        const start = dataInicio ? new Date(`${dataInicio}T00:00:00`) : null;
        const end = dataFim ? new Date(`${dataFim}T23:59:59`) : null;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
    }
    if (!busca) return arr;
    const b = busca.toLowerCase();
    return arr.filter(os =>
      (os.clienteNome || '').toLowerCase().includes(b) ||
      (os.veiculoDescricao || '').toLowerCase().includes(b) ||
      (os.status || '').toLowerCase().includes(b) ||
      String(os.numeroOS || '').includes(b)
    );
  }, [osList, busca, activeTab, dataInicio, dataFim, osNotasSelecionada]);

  if (loading) return <div className="p-6 text-center">A carregar dados...</div>;

  return (
    <div className="flex h-full relative">
      <main className="flex-grow w-full max-w-[1700px] mx-auto p-6">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">Ordens de Serviço</h3>
          
          <div className="flex items-center gap-2">
            <Link
              to="/tutorial#os-basico"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white text-gray-800 dark:text-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Abrir tutorial (OS)"
            >
              <BookOpen className="h-4 w-4" />
              Tutorial
            </Link>

            <button
              onClick={handleNovaOS}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <FaPlus className="h-4 w-4" />
              Nova OS
            </button>
          </div>
        </div>

        {/* Abas + Filtros */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
              <button onClick={() => setActiveTab('abertas')} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'abertas' ? 'bg-white dark:bg-gray-900 shadow' : 'text-gray-600 dark:text-gray-300'}`}>Em Aberto</button>
              <button onClick={() => setActiveTab('historico')} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'historico' ? 'bg-white dark:bg-gray-900 shadow' : 'text-gray-600 dark:text-gray-300'}`}>Histórico (Finalizadas)</button>
            </div>

            <div className="relative w-full md:w-auto md:flex-grow">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Buscar por cliente, veículo, status ou nº OS" className="border px-3 py-2 pl-10 rounded-md w-full bg-white dark:bg-gray-700 dark:border-gray-600" value={busca} onChange={e => setBusca(e.target.value)} />
            </div>

            <button onClick={() => setViewMode(v => (v === 'cards' ? 'table' : 'cards'))} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600">
              {viewMode === 'cards' ? <FaTable /> : <FaThLarge />}
            </button>
          </div>

          {activeTab === 'historico' && (
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Filtrar por data:</span>
              <div className="flex items-center gap-2">
                <label htmlFor="dataInicio" className="text-sm">De:</label>
                <input type="date" id="dataInicio" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="border px-2 py-1 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="dataFim" className="text-sm">Até:</label>
                <input type="date" id="dataFim" value={dataFim} onChange={e => setDataFim(e.target.value)} className="border px-2 py-1 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 text-sm" />
              </div>
            </div>
          )}
        </div>

        {/* Grid/Tabela */}
        {viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {osFiltradas.length > 0 ? osFiltradas.map(os => (
              <OSCard
                key={os.id}
                os={os}
                statusColors={statusColors}
                tempoDesde={tempoDesde}
                formatarData={formatarData}
                onEditar={handleEditarOS}
                onExcluir={handleExcluirOS}
                onAbrirDetalhe={() => setDetalheAberto(os)}
                onIniciarAtendimento={() => handleUpdateStatus(os, 'Em andamento')}
                onFinalizarOS={() => handleUpdateStatus(os, 'Finalizada')}
                onAbrirNotasFiscais={handleAbrirNotasFiscais}
                onEmitirNota={handleEmitirNota}
                onConsultarNfse={handleConsultarNfse}
                onConsultarNfe={handleConsultarNfe}
                nfseLoading={nfseLoading}
                nfeLoading={nfeLoading}
                nfStatusView={nfStatusView}
                nfseStatusView={nfseStatusView}
              />
            )) : (
              <p className="text-gray-500 col-span-full text-center py-8">Nenhuma OS encontrada.</p>
            )}
          </div>
        ) : (
          <OSTable
            osFiltradas={osFiltradas}
            statusColors={statusColors}
            formatarData={formatarData}
            onEditar={handleEditarOS}
            onExcluir={handleExcluirOS}
            onAbrirDetalhe={(os) => setDetalheAberto(os)}
            onIniciarAtendimento={(os) => handleUpdateStatus(os, 'Em andamento')}
            onFinalizarOS={(os) => handleUpdateStatus(os, 'Finalizada')}
            onAbrirNotasFiscais={handleAbrirNotasFiscais}
            onEmitirNota={handleEmitirNota}
            onConsultarNfse={handleConsultarNfse}
            onConsultarNfe={handleConsultarNfe}
            nfseLoading={nfseLoading}
            nfeLoading={nfeLoading}
            nfStatusView={nfStatusView}
            nfseStatusView={nfseStatusView}
          />
        )}

        {/* Modais */}
        {formVisivel && (
          <ModalNovaOS
            key={editandoOs ? `edit-${editandoOs.id}` : 'new'}
            open={true}
            onClose={() => setFormVisivel(false)}
            onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
            onSave={handleSalvarOS}
            form={form}
            clientes={clientes}
            veiculos={veiculos}
            produtos={produtos}
            servicos={servicos}
            editando={!!editandoOs}
          />
        )}

        {!!detalheAberto && (
          <OSDetalheModal open={true} os={detalheAberto} onClose={() => setDetalheAberto(null)} statusColors={statusColors} />
        )}

        {modalNotasOpen && (
          <ModalNotasFiscais
            open={true}
            onClose={() => setModalNotasOpen(false)}
            notas={getNotasFiscaisFromOS(osNotasSelecionada)}
            os={osNotasSelecionada}
            empresaInfo={empresaInfo}
            onCancelarNota={handleCancelarNota}
            loadingCancel={loadingCancelNota}
            onAtualizarNota={handleAtualizarNota}
            loadingRefresh={loadingRefreshNota}
          />
        )}
      </main>
    </div>
  );
}
